import { Box, CircularProgress } from '@mui/material';
import { ReactNode, useContext, useEffect, useState } from 'react';

import { AuthStatus } from './auth-status';
import { subscribeToAuthInvalidation } from './invalidate-session';
import { NoJwtScreen } from './no-jwt-screen.component';
import { NoPermissionsScreen } from './no-permissions-screen.component';
import { resolveAuthStatus } from './resolve-auth-status';
import { UserContext } from './user-context';

/**
 * Route guard placed inside `UserProvider`. On mount it resolves the
 * `AuthStatus` from the `jwt` cookie and decides what to render:
 *
 * - `pending`: a spinner while the cookie is read and (for non-`superadmin`
 *   users) `GET /users/me` resolves the caller's email.
 * - `no-jwt`: `<NoJwtScreen />` (session required). The user clicks
 *   "Iniciar sesión" manually — there is no automatic redirect, so
 *   developers can paste a JWT cookie in DevTools while testing in stage.
 * - `forbidden`: `<NoPermissionsScreen />` with a "Cerrar sesión" action.
 * - `ok`: populates `UserContext` and renders `children`.
 *
 * Acceso: `superadmin` (del JWT) o email interno (`@cliengo`/`@leadaki`,
 * resuelto vía `/users/me`). El privilegio `admin` por sí solo NO alcanza.
 *
 * Subscribes to `auth:invalidated` to re-evaluate whenever the session is
 * dropped (by the 401 interceptor or the NoPermissionsScreen logout).
 */
export const AuthenticationGuard = ({ children }: { children: ReactNode }) => {
  const {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    setJwt,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    setIsAdmin,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    setIsSuperAdmin,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    setName,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    setEmail,
  } = useContext(UserContext);
  const [status, setStatus] = useState<AuthStatus>({ kind: 'pending' });

  useEffect(() => {
    let cancelled = false;

    const resolveAndApply = async () => {
      setStatus({ kind: 'pending' });

      const next = await resolveAuthStatus();

      if (cancelled) {
        return;
      }

      if (next.kind === 'ok') {
        setJwt(next.jwt);
        setIsAdmin(next.isAdmin);
        setIsSuperAdmin(next.isSuperAdmin);
        setName(next.user?.name ?? '');
        setEmail(next.user?.email ?? '');
      } else {
        setJwt('');
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setName('');
        setEmail('');
      }

      setStatus(next);
    };

    void resolveAndApply();

    const unsubscribe = subscribeToAuthInvalidation(() => {
      void resolveAndApply();
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status.kind === 'pending') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress aria-label="Verificando sesión" />
      </Box>
    );
  }

  if (status.kind === 'no-jwt') {
    return <NoJwtScreen />;
  }

  if (status.kind === 'forbidden') {
    return <NoPermissionsScreen />;
  }

  return <>{children}</>;
};
